import { Column } from '../types/WorkspaceViewColumn';
import { WorkspaceEstimationType, WorkspaceFramework } from '../types/Workspace';
import { TaskTableDataType } from '../components/TaskTable/types/TaskTableTypes';
import { COLUMN_WIDTHS } from '../components/TaskTable/constants/columnWidths';
import { WorkspaceViewEntityType, WorkspaceViewGroup } from '../types/WorkspaceView';
import { TaskStage } from '../types/Task';
import { TaskType } from '../types/TaskType';
import { SprintStatusType } from '../components/UI/SprintStatusLabel';
import {
  ColumnVisibilityContext,
  isColumnVisible,
  isExpandColumnVisible,
} from '../components/TaskManagement/VirtualizedTaskList/columnVisibility';

/**
 * Checks if two tasks belong to the same sort group for a given sort column.
 * Used by drag-and-drop to validate whether reordering is allowed.
 */
export function isSameSortGroup(
  active: TaskTableDataType,
  over: TaskTableDataType,
  sortByColumn: string | undefined,
  estimationType: WorkspaceEstimationType,
  activeSprintId?: number | null
): boolean {
  if (!sortByColumn) return true;
  switch (sortByColumn) {
    case Column.assignee:
      return (active.assignee ?? null) === (over.assignee ?? null);
    case Column.status:
      return active.statusId === over.statusId;
    case Column.estimate:
      if (estimationType === WorkspaceEstimationType.tshirtSizes) {
        return (active.estimate ?? null) === (over.estimate ?? null);
      }
      return (active.storyPoints ?? null) === (over.storyPoints ?? null);
    case Column.priority:
      return active.priority === over.priority;
    case Column.epic:
      return (active.epicId ?? null) === (over.epicId ?? null);
    case Column.startDate:
      return String(active.startDate ?? '') === String(over.startDate ?? '');
    case Column.deadline:
      return String(active.deadline ?? '') === String(over.deadline ?? '');
    case Column.createdAt:
      return String(active.createdAt ?? '') === String(over.createdAt ?? '');
    case Column.updatedAt:
      return String(active.updatedAt ?? '') === String(over.updatedAt ?? '');
    case Column.sprintStatus:
      // Compare the effective stage, not raw sprintId: a Backlog row and a Planning
      // row both have `sprintId: null` but render in different Stage blocks. #2970
      return getEffectiveStage(active, activeSprintId) === getEffectiveStage(over, activeSprintId);
    case Column.taskId:
      return active.taskId === over.taskId;
    case Column.taskType:
      return active.type === over.type;
    default:
      return true;
  }
}

export const SPACE_COLUMN_WIDTH = 120;

export type CalculateNonTitleWidthParams = {
  operationWidth: number;
  hiddenColumns: Column[];
  viewEntityType: string | undefined;
  framework: WorkspaceFramework;
  viewGroup: WorkspaceViewGroup | undefined;
  showSpaceColumn?: boolean;
};

/**
 * Calculates the total pixel width of all non-title columns.
 * Used to derive the responsive title column width.
 */
export function calculateNonTitleWidth({
  operationWidth,
  hiddenColumns,
  viewEntityType,
  framework,
  viewGroup,
  showSpaceColumn,
}: CalculateNonTitleWidthParams): number {
  let total = operationWidth + COLUMN_WIDTHS.taskId;

  // Reserve width for exactly the columns StickyColumnHeader and VirtualizedTaskRow
  // will render — they all consult the same predicate. (#2544)
  const ctx: ColumnVisibilityContext = {
    hiddenColumns,
    viewEntityType: viewEntityType as WorkspaceViewEntityType | undefined,
    viewGroup,
    framework,
  };
  const visible = (column: Column) => isColumnVisible(column, ctx);

  if (isExpandColumnVisible(ctx)) total += 20;

  if (visible(Column.subitems)) total += COLUMN_WIDTHS.subitems;
  if (visible(Column.epic)) total += COLUMN_WIDTHS.epicId;
  if (visible(Column.sprintStatus)) total += COLUMN_WIDTHS.sprintId;
  if (visible(Column.label)) total += COLUMN_WIDTHS.TaskLabels;
  if (visible(Column.assignee)) total += COLUMN_WIDTHS.assignee;
  if (visible(Column.status)) total += COLUMN_WIDTHS.statusId;
  if (visible(Column.startDate)) total += COLUMN_WIDTHS.startDate;
  if (visible(Column.deadline)) total += COLUMN_WIDTHS.deadline;
  if (visible(Column.blocking)) total += COLUMN_WIDTHS.blocking;
  if (visible(Column.blockedBy)) total += COLUMN_WIDTHS.blockedBy;
  if (visible(Column.priority)) total += COLUMN_WIDTHS.priority;
  if (visible(Column.estimate)) total += COLUMN_WIDTHS.estimate;
  total += COLUMN_WIDTHS.space;
  if (showSpaceColumn && visible(Column.space)) total += SPACE_COLUMN_WIDTH;

  return total;
}

export type SprintStatusValue = SprintStatusType;

export type GetSprintStatusParams = {
  stage: TaskStage | undefined;
  isScrum: boolean;
};

/**
 * Maps the authoritative `task.stage` to the framework-aware presentational
 * `SprintStatusType` (which uses `'none'` for backlog and `'kanban'` instead of
 * `'sprint'` in kanban workspaces).
 */
export function getSprintStatus({ stage, isScrum }: GetSprintStatusParams): SprintStatusValue {
  switch (stage) {
    case 'triage':
      return 'triage';
    case 'planning':
      return 'planning';
    case 'sprint':
      return isScrum ? 'sprint' : 'kanban';
    case 'archive':
      return 'archive';
    case 'backlog':
    default:
      return 'none';
  }
}

/**
 * Authoritative sprint-membership test. A task is "in the sprint" iff it is
 * assigned to the workspace's ACTIVE sprint — the exact predicate the Sprint
 * module (useManageTasks) and the server (getSprintTasks) use. `task.stage`
 * must never be trusted for this: it drifts from `sprintId` (e.g. the server's
 * end-sprint routine resets only unfinished tasks, so completed tasks keep
 * `stage:'sprint'` with a stale `sprintId` forever). #2598
 */
export function isInActiveSprint(
  task: { sprintId?: number | null },
  activeSprintId: number | null | undefined
): boolean {
  return !!activeSprintId && task.sprintId === activeSprintId;
}

/**
 * The sprint-status chip to DISPLAY for a live task, derived from the same
 * authoritative membership as the Sprint module so the "Stage" column, the
 * "Stage" filter and the Sprint module can never disagree. #2598
 *  - In the active sprint (by sprintId)            -> 'sprint'/'kanban'
 *  - `stage:'sprint'` but NOT in the active sprint -> 'none' (Backlog); it is a
 *    stale/drifted chip and must read as Backlog, matching the module excluding it
 *  - otherwise fall back to the raw stage (planning/triage/archive/backlog)
 *
 * For historical stage snapshots (activity log) use `getSprintStatus` instead —
 * those have no live `sprintId`/active-sprint context.
 */
export function getEffectiveSprintStatus({
  task,
  isScrum,
  activeSprintId,
}: {
  task: { stage?: TaskStage; sprintId?: number | null };
  isScrum: boolean;
  activeSprintId?: number | null;
}): SprintStatusValue {
  if (isInActiveSprint(task, activeSprintId)) {
    return isScrum ? 'sprint' : 'kanban';
  }
  if (task.stage === 'sprint') {
    return 'none';
  }
  return getSprintStatus({ stage: task.stage, isScrum });
}

export function sprintStatusToStage(sprintStatus: SprintStatusValue): TaskStage {
  switch (sprintStatus) {
    case 'triage':
      return 'triage';
    case 'planning':
      return 'planning';
    case 'sprint':
    case 'kanban':
      return 'sprint';
    case 'archive':
      return 'archive';
    case 'none':
    default:
      return 'backlog';
  }
}

/**
 * The stage a task effectively sits in, as a framework-independent `TaskStage`.
 * Same derivation as the chip the Stage column renders, so anything that orders or
 * groups by stage agrees with what the user sees. `isScrum` is irrelevant here:
 * `sprintStatusToStage` collapses 'sprint' and 'kanban' back to the one stage. #2969
 */
export function getEffectiveStage(
  task: { stage?: TaskStage; sprintId?: number | null },
  activeSprintId?: number | null
): TaskStage {
  return sprintStatusToStage(getEffectiveSprintStatus({ task, isScrum: true, activeSprintId }));
}

/** The fields sprint membership is decided on. */
export type SprintCountableTask = {
  id: number;
  type?: string;
  stage?: TaskStage;
  sprintId?: number | null;
  parentTaskId?: number | null;
  archivedAt?: string | Date | null;
};

/**
 * The tasks a sprint's counters may count: real, listed work items in the active
 * sprint.
 *
 * The two existing counters disagreed because they read different sources. The
 * sprint badge derives from the task list, which already drops epics, subitems
 * and archived rows; the End-Sprint modal read the sprint endpoint's payload,
 * which is `findMany({ where: { sprintId } })` server-side with no exclusions at
 * all. So the modal counted epics, subitems and tasks archived while still
 * carrying a sprintId — 13 against a badge of 7. Both now count through here, so
 * they agree by construction rather than by being kept in sync. #2956, #2957
 *
 * Membership stays `sprintId`-based (via `getEffectiveStage`) rather than raw
 * `stage`, so this cannot disagree with the Stage chip the same row renders. #2598
 */
export function selectSprintCountableTasks<T extends SprintCountableTask>(
  tasks: T[] | undefined | null,
  activeSprintId: number | null | undefined
): T[] {
  if (!tasks?.length || !activeSprintId) return [];
  const seen = new Set<number>();
  return tasks.filter((task) => {
    if (task.type === 'epic') return false;
    if (task.parentTaskId != null) return false;
    if (task.archivedAt) return false;
    if (getEffectiveStage(task, activeSprintId) !== 'sprint') return false;
    if (seen.has(task.id)) return false;
    seen.add(task.id);
    return true;
  });
}

export type SprintStatusTaskUpdates = {
  stage: TaskStage;
  sprintId: number | null;
  planningSessionId?: number | null;
};

/**
 * Single source of truth for the field changes a stage/sprintStatus selection
 * implies. The Sprint module scopes tasks by `sprintId === activeSprintId`, not
 * by `stage`, so the two MUST be written together — otherwise a task can show a
 * "Sprint" stage chip yet be absent from the sprint module (or vice versa).
 * Every place that changes a task's stage must apply these updates. #2598
 */
export function sprintStatusToTaskUpdates(
  sprintStatus: SprintStatusValue,
  activeSprintId?: number | null
): SprintStatusTaskUpdates {
  const stage = sprintStatusToStage(sprintStatus);
  if (stage === 'sprint' && activeSprintId) {
    return { stage, sprintId: Number(activeSprintId), planningSessionId: null };
  }
  if (stage === 'planning') {
    return { stage, sprintId: null };
  }
  return { stage, sprintId: null, planningSessionId: null };
}

export type SprintStatusOption = {
  id: number;
  sprintStatus: SprintStatusValue;
  name: string;
};

const SPRINT_STATUS_LABEL: Record<SprintStatusValue, string> = {
  triage: 'Triage',
  planning: 'Planning',
  sprint: 'Sprint',
  kanban: 'Kanban',
  none: 'Backlog',
  archive: 'Archive',
};

/**
 * Returns every stage option that can appear in the stage dropdown for the
 * given workspace framework. The order matches the dropdown's visual order
 * (triage → backlog → planning/sprint or kanban → archive). Sprint/Kanban
 * targets are omitted when there's no active sprint to send the task to.
 */
export function getPossibleSprintStatuses(
  isScrum: boolean,
  activeSprintId: number | string | undefined
): SprintStatusOption[] {
  const result: SprintStatusOption[] = [];
  let idx = 0;
  const push = (sprintStatus: SprintStatusValue) => {
    result.push({ id: idx++, sprintStatus, name: SPRINT_STATUS_LABEL[sprintStatus] });
  };

  push('triage');
  push('none');
  if (isScrum) {
    push('planning');
    if (activeSprintId) push('sprint');
  } else if (activeSprintId) {
    push('kanban');
  }
  push('archive');

  return result;
}

/** The fields a task list's view scoping is decided on. */
export type ViewScopableTask = {
  id: number;
  type?: string;
  stage?: TaskStage;
  sprintId?: number | null;
  parentTaskId?: number | null;
};

/**
 * The rows a task-entity list view shows, before filters, grouping and sorting. This is
 * the one place that decides which tasks belong to Triage, Backlog, Sprint/Kanban and
 * Archive, so the table (useManageTasks) and the Task view's prev/next arrows
 * (useTaskNavigation) walk the same list. #3242
 *  - Sprint/Kanban: tasks in the active sprint (any task with a sprint when none is active)
 *  - Triage: `stage === 'triage'`; subitems are listed flat, so they stay in
 *  - Archive: every task of the source (the caller passes the archived list)
 *  - anything else (Backlog): everything not in triage
 * Epics are never rows, and outside Triage neither are subitems (they nest under their
 * parent) unless `keepSubitems` is set. For a duplicated id the last copy is kept.
 */
export function scopeTasksForView<T extends ViewScopableTask>(
  tasks: T[],
  group: WorkspaceViewGroup | undefined,
  activeSprintId: number | null | undefined,
  { keepSubitems = false }: { keepSubitems?: boolean } = {}
): T[] {
  const isTriageView = group === WorkspaceViewGroup.triage;
  const scoped =
    group === WorkspaceViewGroup.sprint || group === WorkspaceViewGroup.kanban
      ? tasks.filter((task) => (activeSprintId ? task.sprintId === activeSprintId : task.sprintId))
      : isTriageView
        ? tasks.filter((task) => task.stage === 'triage')
        : group === WorkspaceViewGroup.archive
          ? tasks
          : tasks.filter((task) => task.stage !== 'triage');
  // Precomputed so this stays linear on large spaces; it re-runs on every list refetch. (#2803)
  const lastIndexById = new Map<number, number>();
  scoped.forEach((task, index) => lastIndexById.set(task.id, index));
  return scoped.filter((task, index) => {
    if (task.type === TaskType.epic) return false;
    if (!isTriageView && !keepSubitems && task.parentTaskId !== null) return false;
    return lastIndexById.get(task.id) === index;
  });
}
